import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button, Icon, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { audioState, onAudioStateChange, playChime, primeAudio, setSoundEnabled, soundEnabled } from "../lib/chime.js";
import { isAttending, isShowing } from "../lib/attention.js";
import { closeSystemNotifications } from "../lib/push.js";
import { useRealtimeFrames, useReconnected } from "../lib/realtime-context.js";
import { useWorkspace } from "../lib/workspace.js";
import { isDesktop, notifyNative, setNativeBadge, takePendingDestination } from "../lib/transport.js";
import "./notifications.css";

/**
 * The notification bell and its panel.
 *
 * Lives in the shell rather than a route so it is reachable from anywhere —
 * a notification you have to navigate to in order to see is one you will miss.
 * New ones arrive over the existing WebSocket, so the count is live without
 * polling.
 */

type Notification = {
  id: string;
  type: string;
  title: string;
  body: string | null;
  resourceType: string | null;
  resourceId: string | null;
  messageId: string | null;
  /** The office the resource lives in (null when none), so a click from
   *  another office can switch to it before navigating. */
  workspaceId: string | null;
  readAt: string | null;
  createdAt: string;
};

/** Where a notification points, or null when it names nothing navigable. */
function destinationOf(notification: Notification): string | null {
  if (!notification.resourceId) return null;
  switch (notification.resourceType) {
    case "work":
      // The batched due-today/overdue nudge: Work, narrowed to my own tasks.
      return "/work?mine=1";
    case "task":
      return `/work/task/${notification.resourceId}`;
    case "project":
      return `/work/project/${notification.resourceId}`;
    case "channel":
      // A mention or message notification knows which message it was about —
      // land on that exact message rather than just the top of the channel,
      // so the click drops the reader into the same spot they were pinged on.
      //
      // A thread reply's messageId is the thread's root, which can be months
      // up the channel; opening the thread shows the reply itself instead.
      if (notification.messageId && notification.type === "thread.reply") {
        return `/chat/${notification.resourceId}?thread=${notification.messageId}`;
      }
      return notification.messageId
        ? `/chat/${notification.resourceId}?message=${notification.messageId}`
        : `/chat/${notification.resourceId}`;
    case "event":
      // Schedule has no per-event route — everything lives on `/schedule` with
      // the focused event carried as a query param, same pattern as `?message=`.
      return `/schedule?event=${notification.resourceId}`;
    case "post":
      return `/feed?post=${notification.resourceId}`;
    case "doc":
      // Resolves the page's project and opens its Docs tab.
      return `/work/doc/${notification.resourceId}`;
    case "insights":
      // The weekly manager report: the portfolio it summarises lives here.
      return "/insights";
    default:
      return null;
  }
}

/** The push tags (see `notify` in the API) for a set of notifications. */
function tagsOf(notifications: Notification[]): string[] {
  const tags = new Set<string>();
  for (const n of notifications) tags.add(n.resourceId ? `${n.resourceType}:${n.resourceId}` : n.type);
  return [...tags];
}

function iconFor(type: string): "check" | "chat" | "work" | "bell" {
  if (type.startsWith("task.")) return "check";
  if (type.startsWith("message.") || type.startsWith("channel.")) return "chat";
  if (type.startsWith("project.") || type.startsWith("doc.")) return "work";
  return "bell";
}

/**
 * One row per conversation, not per message.
 *
 * Ten messages in a channel are one thing you need to know about, not ten —
 * and listing them individually means ten clicks to clear a single unread
 * conversation, each landing on the same screen. Grouped by what the
 * notification points at, so opening it clears the whole group.
 *
 * Only message notifications group. A task assignment and a task comment on
 * the same task are genuinely different events, and collapsing them would hide
 * one behind the other.
 */
type NotificationGroup = {
  /** The newest member, which supplies the title, body and timestamp. */
  latest: Notification;
  /** Every notification in the group, newest first — all cleared together. */
  all: Notification[];
  unreadCount: number;
};

function groupNotifications(items: Notification[]): NotificationGroup[] {
  const groups: NotificationGroup[] = [];
  const byKey = new Map<string, NotificationGroup>();

  for (const item of items) {
    const groupable = item.type.startsWith("message.") && item.resourceId;
    const key = groupable ? `${item.resourceType}:${item.resourceId}` : `id:${item.id}`;

    const existing = byKey.get(key);
    if (existing) {
      existing.all.push(item);
      if (!item.readAt) existing.unreadCount += 1;
      continue;
    }

    // `items` arrives newest-first, so the first one seen is the latest.
    const group: NotificationGroup = { latest: item, all: [item], unreadCount: item.readAt ? 0 : 1 };
    byKey.set(key, group);
    groups.push(group);
  }

  return groups;
}

function relativeTime(iso: string): string {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** Full local timestamp for a row's `title` — the only place the exact time
 * is available once the display has been reduced to "3h" or "Mon 26 Aug". */
function absoluteTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/** Midnight-to-midnight day key in the viewer's own zone, so grouping follows
 * the calendar day they are looking at rather than UTC. */
function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** Today / Yesterday / a short absolute date — the same three tiers Slack and
 * Linear use, so a list spanning weeks does not read as a wall of "3d", "4d". */
function dayLabel(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(date)) / 86_400_000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return date.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

export function Notifications({ railExpanded = false }: { railExpanded?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { openIn } = useWorkspace();
  /** Read once: the stored preference does not change from another tab. */
  const [sound, setSound] = useState(() => soundEnabled());
  /** Whether audio has actually unlocked, which happens after a gesture. */
  const [audio, setAudio] = useState(() => audioState());
  const { toast } = useToast();

  const [items, setItems] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  /** Read once: the stored preference does not change from another tab. */
  const [filter, setFilter] = useState<"all" | "unread">(() => {
    try {
      return localStorage.getItem("astrum.notifications.filter") === "unread" ? "unread" : "all";
    } catch {
      return "all";
    }
  });
  const wrapRef = useRef<HTMLDivElement | null>(null);
  /** Read from the frame handler, which should not re-subscribe per render. */
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const setFilterPersisted = useCallback((next: "all" | "unread") => {
    setFilter(next);
    try {
      localStorage.setItem("astrum.notifications.filter", next);
    } catch {
      // Best effort — a private tab or a full quota just means the choice
      // does not survive reopening the panel.
    }
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const query = filter === "unread" ? "?limit=20&unreadOnly=true" : "?limit=20";
    api
      .get<{ notifications: Notification[]; unreadCount: number; hasMore: boolean }>(`/notifications${query}`)
      .then((res) => {
        setItems(res.notifications);
        setUnreadCount(res.unreadCount);
        setHasMore(res.hasMore);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [filter]);

  /**
   * Fetches the next page and appends it.
   *
   * The cursor is the oldest loaded item's id: notification ids are UUIDv7
   * (chronologically sortable), and the API filters with `id < before`, so
   * naming the last row in `items` picks up exactly where this page left off
   * regardless of how many were read or dismissed since.
   */
  const loadMore = useCallback(() => {
    const last = items[items.length - 1];
    if (!last) return;
    const before = last.id;
    setLoadingMore(true);
    const params = new URLSearchParams({ limit: "20", before });
    if (filter === "unread") params.set("unreadOnly", "true");
    api
      .get<{ notifications: Notification[]; unreadCount: number; hasMore: boolean }>(`/notifications?${params}`)
      .then((res) => {
        setItems((prev) => [...prev, ...res.notifications]);
        setUnreadCount(res.unreadCount);
        setHasMore(res.hasMore);
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not load more notifications.", { tone: "danger" });
      })
      .finally(() => setLoadingMore(false));
  }, [items, filter, toast]);

  /*
   * Re-read on every navigation, not only on mount.
   *
   * The socket tells us about notifications arriving, but nothing tells us
   * about them being cleared: opening a task or a channel marks its own
   * notifications read on the server, and without this the badge kept
   * counting them until a full page reload. Reading is the common case, so a
   * badge that only ever goes up is a badge people stop believing.
   */
  useEffect(() => load(), [load, location.pathname]);

  // Notifications that arrived while the socket was down were never framed;
  // the list and badge are fetched again once it is back.
  useReconnected(load);

  // Audio cannot start until the user has touched the page, so the context is
  // created on their first gesture rather than on the first notification.
  useEffect(() => primeAudio(), []);

  // The unlock lands after the render that asked about it, so the control is
  // told rather than left reporting a state that has since changed.
  useEffect(() => onAudioStateChange(() => setAudio(audioState())), []);

  useRealtimeFrames((frame) => {
    /*
     * Cleared elsewhere — another tab, another device, or a channel opened
     * somewhere else. The count is taken from the frame rather than
     * decremented: the server's number is authoritative, and a client that
     * missed a frame would otherwise drift and never recover.
     */
    if (frame.t === "notification_read") {
      const { ids } = frame;
      setUnreadCount(frame.unreadCount);
      setItems((prev) =>
        prev.map((n) =>
          (ids === null || ids.includes(n.id)) && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n,
        ),
      );
      // The OS copies of what was just read go too, or they linger in the
      // notification centre announcing messages already answered.
      void closeSystemNotifications(ids === null ? null : tagsOf(itemsRef.current.filter((n) => ids.includes(n.id))));
      return;
    }
    if (frame.t !== "notification") return;
    const notification = frame.notification as Notification;
    const destination = destinationOf(notification);

    /*
     * Arrived for the screen the person is looking at right now.
     *
     * Chat marks a visible conversation read as each message lands, but that
     * read can reach the server a moment *before* this row is inserted — the
     * two race — and then it stayed unread forever, a bell count for a message
     * already on screen. Settling it here closes that gap whichever side wins.
     */
    const alreadyThere = destination !== null && isShowing(location.pathname, destination);
    if (alreadyThere && isAttending()) {
      setItems((prev) => [{ ...notification, readAt: new Date().toISOString() }, ...prev]);
      api.post("/notifications/read", { ids: [notification.id] }).catch(() => undefined);
      return;
    }

    // Unbounded: a hard slice here would silently drop rows already loaded
    // further down the list (including anything brought in by "Show more"),
    // which is exactly the bug that made everything past the first page
    // unreachable. Pagination trims from the tail instead, at `loadMore`.
    setItems((prev) => [notification, ...prev]);
    setUnreadCount((prev) => prev + 1);

    /*
     * Sound and an OS popup, for something the person is not already looking
     * at — the on-screen case returned above.
     *
     * The server has already decided this notification is worth sending — it
     * applies the channel's all/mentions/none setting and never notifies you
     * about your own message — so the only judgement left here is about
     * attention. The window being focused is not enough on its own: the app
     * may be in front while the message belongs to a different channel, which
     * is exactly when a notification is useful. And the right route being
     * open is not enough either: a window hidden in the tray or behind another
     * app shows nobody anything.
     */
    // Do Not Disturb or quiet hours (decided by the server, in the person's own
    // timezone): the row and badge above still land, but nothing makes a sound
    // or raises an OS popup.
    if (frame.quiet) return;
    playChime();
    void notifyNative(notification.title, notification.body, destination);
  });

  /* Escape and click-outside, so the panel is dismissible without hunting for
   * the button that opened it. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open]);

  const markAllRead = useCallback(() => {
    // Optimistic: the badge should clear the instant it is clicked, and a
    // failed read is recoverable on next load.
    setItems((prev) => prev.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() })));
    setUnreadCount(0);
    api.post("/notifications/read-all").catch((error: unknown) => {
      toast(error instanceof ApiError ? error.message : "Could not mark those as read.", { tone: "danger" });
      load();
    });
  }, [load, toast]);

  /**
   * Marks one group read without navigating or closing the panel.
   *
   * Separate from `openGroup`: that action means "I looked at this", which
   * implies going there. This one is for clearing a row you have already
   * seen enough of — e.g. from the subject line alone — without leaving the
   * list you are scanning.
   */
  const markGroupRead = useCallback(
    (group: NotificationGroup) => {
      const unreadIds = group.all.filter((n) => !n.readAt).map((n) => n.id);
      if (unreadIds.length === 0) return;
      const now = new Date().toISOString();
      const unread = new Set(unreadIds);
      setItems((prev) => prev.map((n) => (unread.has(n.id) ? { ...n, readAt: now } : n)));
      setUnreadCount((prev) => Math.max(0, prev - unreadIds.length));
      api.post("/notifications/read", { ids: unreadIds }).catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not mark that as read.", { tone: "danger" });
        load();
      });
    },
    [load, toast],
  );

  /**
   * Opening a group clears every notification in it, not just the one shown.
   *
   * The row stands for a conversation, and opening that conversation means you
   * have seen all of it — leaving the rest unread would put the badge straight
   * back after the click that was meant to clear it.
   */
  const openGroup = useCallback(
    (group: NotificationGroup) => {
      const unreadIds = group.all.filter((n) => !n.readAt).map((n) => n.id);
      if (unreadIds.length > 0) {
        const now = new Date().toISOString();
        const unread = new Set(unreadIds);
        setItems((prev) => prev.map((n) => (unread.has(n.id) ? { ...n, readAt: now } : n)));
        setUnreadCount((prev) => Math.max(0, prev - unreadIds.length));
        api.post("/notifications/read", { ids: unreadIds }).catch(() => undefined);
      }

      // Only close the panel when the click is actually going somewhere.
      // Closing implies something happened; for a destination-less
      // notification nothing did, and closing anyway makes the click look
      // like it silently failed instead of just having nowhere to go.
      const to = destinationOf(group.latest);
      if (to) {
        setOpen(false);
        // A mention in another office points at a channel this office cannot
        // see; switch to its office first or the click lands on "not found".
        openIn(group.latest.workspaceId, () => navigate(to));
      }
    },
    [navigate, openIn],
  );

  /** Dismissing a group removes every notification behind it. */
  const dismissGroup = useCallback(
    (group: NotificationGroup) => {
      const ids = new Set(group.all.map((n) => n.id));
      setItems((prev) => prev.filter((n) => !ids.has(n.id)));
      setUnreadCount((prev) => Math.max(0, prev - group.unreadCount));

      Promise.all(group.all.map((n) => api.delete(`/notifications/${n.id}`)))
        // Deleting is permanent and the × used to say only "Dismiss", which
        // read as reversible. `useToast` has no action-button option (see
        // ToastOptions in @astrum/ui), so there is no undo to offer here —
        // this at least confirms the removal happened rather than the row
        // just quietly vanishing.
        .then(() => toast("Removed"))
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not remove that.", { tone: "danger" });
          load();
        });
    },
    [load, toast],
  );

  /*
   * The dock/taskbar badge follows the same number the bell shows.
   *
   * An effect on the count rather than a call beside each of the five places
   * that change it: reading, dismissing, marking all read, arriving, and the
   * initial load all have to agree, and the one that gets forgotten is how a
   * badge ends up claiming unread messages that were read an hour ago.
   */
  useEffect(() => {
    void setNativeBadge(unreadCount);
  }, [unreadCount]);

  /*
   * Clicking a native notification (or following an astrum-connect:// link)
   * makes the shell hold the destination and fire `astrum:destination`, and it
   * is claimed here. Also on mount, for a click that started the app.
   *
   * Not on focus: older shells stored the destination when the notification
   * was shown, so claiming on focus sent someone who merely switched back to
   * the window to whichever chat last notified them, every time.
   */
  useEffect(() => {
    if (!isDesktop) return;

    const claim = () => {
      void takePendingDestination().then((destination) => {
        if (destination) navigate(destination);
      });
    };

    claim();
    window.addEventListener("astrum:destination", claim);
    return () => {
      window.removeEventListener("astrum:destination", claim);
    };
  }, [navigate]);

  const groups = useMemo(() => groupNotifications(items), [items]);

  /** Groups bucketed under a Today / Yesterday / date header, in arrival
   * order — the conversation grouping above happens first, so a day section
   * still collapses repeat messages the same way the flat list did. */
  const dayBuckets = useMemo(() => {
    const buckets: { key: string; label: string; groups: NotificationGroup[] }[] = [];
    for (const group of groups) {
      const key = dayKey(group.latest.createdAt);
      const bucket = buckets[buckets.length - 1];
      if (bucket && bucket.key === key) {
        bucket.groups.push(group);
      } else {
        buckets.push({ key, label: dayLabel(group.latest.createdAt), groups: [group] });
      }
    }
    return buckets;
  }, [groups]);

  const label = useMemo(
    () => (unreadCount > 0 ? `Notifications — ${unreadCount} unread` : "Notifications"),
    [unreadCount],
  );

  return (
    <div className="notifications" ref={wrapRef}>
      <button
        type="button"
        aria-label={label}
        title={railExpanded ? undefined : label}
        className={`notifications__trigger shell-rail-action ${open ? "is-open" : ""}`}
        onClick={() => {
          setOpen((v) => !v);
          if (!open) load();
        }}
      >
        <Icon name="bell" size={18} />
        {railExpanded && <span className="shell-nav-label">Notifications</span>}
      </button>
      {unreadCount > 0 && (
        <span className="notifications__badge" aria-hidden="true">
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}

      {open && (
        <div className="notifications__panel" role="dialog" aria-label="Notifications">
          <div className="notifications__head">
            <h3>Notifications</h3>
            {/* All/Unread, Slack/Linear-style. Unread re-queries the server
                with `unreadOnly=true` rather than filtering client-side, so
                it also sees unread rows outside the currently loaded page. */}
            <div className="notifications__filter" role="group" aria-label="Filter notifications">
              <button
                type="button"
                className={filter === "all" ? "is-active" : ""}
                aria-pressed={filter === "all"}
                onClick={() => setFilterPersisted("all")}
              >
                All
              </button>
              <button
                type="button"
                className={filter === "unread" ? "is-active" : ""}
                aria-pressed={filter === "unread"}
                onClick={() => setFilterPersisted("unread")}
              >
                Unread
              </button>
            </div>
            {/* Where somebody who just heard the sound will look for the way
                to stop hearing it. */}
            <button
              type="button"
              className="notifications__sound"
              aria-pressed={sound}
              /* Says when audio has not unlocked yet, which is otherwise
                 indistinguishable from the sound being broken. */
              title={
                !sound
                  ? "Sound off — click to unmute"
                  : audio === "ready"
                    ? "Sound on — click to mute"
                    : "Sound on, waiting for this tab to allow audio — click to test"
              }
              onClick={() => {
                const next = !sound;
                setSound(next);
                setSoundEnabled(next);
                // Play the sound they just turned on, so the choice is
                // confirmed by the thing itself rather than by a label.
                if (next) playChime();
              }}
            >
              <Icon name={sound ? "bell" : "mute"} size={14} />
              <span className="sr-only">{sound ? "Mute notification sound" : "Unmute notification sound"}</span>
            </button>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllRead}>
                Mark all read
              </Button>
            )}
            <button
              type="button"
              className="notifications__settings"
              title="Notification settings"
              onClick={() => {
                setOpen(false);
                navigate("/profile#notifications");
              }}
            >
              <Icon name="settings" size={14} />
              <span className="sr-only">Notification settings</span>
            </button>
          </div>

          <div className="notifications__scroll">
            {loading && items.length === 0 ? (
              <p className="notifications__empty">Loading…</p>
            ) : items.length === 0 ? (
              <p className="notifications__empty">
                Nothing yet. You will hear about tasks assigned to you, comments on your work, and projects you are
                added to.
              </p>
            ) : (
              <>
                {dayBuckets.map((bucket) => (
                  <ul key={bucket.key}>
                    <li className="notifications__day" role="presentation">
                      {bucket.label}
                    </li>
                    {bucket.groups.map((group) => (
                      <li key={group.latest.id}>
                        <button
                          type="button"
                          className={`notifications__item ${group.unreadCount > 0 ? "is-unread" : ""}`}
                          onClick={() => openGroup(group)}
                        >
                          <span className="notifications__icon">
                            <Icon name={iconFor(group.latest.type)} size={14} />
                          </span>
                          <span className="notifications__text">
                            <strong>
                              {group.unreadCount > 0 && <span className="sr-only">Unread: </span>}
                              {group.latest.title}
                            </strong>
                            {group.latest.body && <span>{group.latest.body}</span>}
                          </span>
                          <span className="notifications__meta">
                            {/* Says how many behind this row are still unread, so
                                the badge agrees with the chat window's own count
                                instead of counting every notification ever
                                grouped here, read or not. */}
                            {group.unreadCount > 1 && (
                              <span className="notifications__count">{group.unreadCount}</span>
                            )}
                            <span className="notifications__time" title={absoluteTime(group.latest.createdAt)}>
                              {relativeTime(group.latest.createdAt)}
                            </span>
                          </span>
                        </button>

                        {group.unreadCount > 0 && (
                          <button
                            type="button"
                            className="notifications__read"
                            aria-label={`Mark as read: ${group.latest.title}`}
                            title="Mark as read"
                            onClick={() => markGroupRead(group)}
                          >
                            <Icon name="check" size={12} />
                          </button>
                        )}

                        <button
                          type="button"
                          className="notifications__dismiss"
                          aria-label={
                            group.all.length > 1
                              ? `Remove ${group.all.length} notifications: ${group.latest.title}`
                              : `Remove: ${group.latest.title}`
                          }
                          title="Remove this notification"
                          onClick={() => dismissGroup(group)}
                        >
                          <Icon name="close" size={12} />
                        </button>
                      </li>
                    ))}
                  </ul>
                ))}
                {hasMore && (
                  <div className="notifications__more">
                    <Button variant="ghost" size="sm" onClick={loadMore} disabled={loadingMore}>
                      {loadingMore ? "Loading…" : "Show more"}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
